import { __awaiter } from 'tslib';
import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Optional, Inject, NgModule } from '@angular/core';
import firebase from 'firebase/compat/app';
import { zip, of, fromEvent, from, BehaviorSubject, ReplaySubject } from 'rxjs';
import { switchMap, map, finalize, take, tap } from 'rxjs/operators';
import * as i1 from '@angular/fire/compat/auth';
import { USE_EMULATOR, AngularFireAuthModule } from '@angular/fire/compat/auth';
import * as i2 from '@angular/router';
import 'firebase/compat/auth';
import 'firebase/compat/database';
import * as queryString from 'query-string';
import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { FIREBASE_OPTIONS, AngularFireModule } from '@angular/fire/compat';

/**
 * Injection token for Deloitte auth strategy we want to use here
 *
 */
const AUTH_STRATEGY = new InjectionToken('Auth Strategy');
const LOGIN_PARAMS = new InjectionToken('Parameters which will passed to login');
function logout(strategy) {
    if (strategy instanceof GoogleAuthStrategy ||
        strategy instanceof EmailPasswordStrategy ||
        strategy instanceof IframeStrategy ||
        strategy instanceof SSOStrategy) {
        return strategy.logout();
    }
}
/**
 * authorize via open authorization service like google, azure
 *
 */
class SSOStrategy {
    constructor(fireAuth, rendererFactory) {
        this.fireAuth = fireAuth;
        this.identifier = ['oidc', 'saml'];
        this.renderer = rendererFactory.createRenderer(null, null);
    }
    login(provider) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.oAuthProvider) {
                this.oAuthProvider = new firebase.auth.OAuthProvider(provider);
                this.oAuthProvider.addScope('User.Read');
            }
            return this.fireAuth.signInWithRedirect(this.oAuthProvider);
        });
    }
    logout() {
        return this.logoutFromMicrosoft().pipe(switchMap(() => this.fireAuth.signOut()));
    }
    logoutFromMicrosoft() {
        /**
         * logout from microsoft account, since the redirect is for microsoft seems to only work
         * if we call the logout url, simply call the url to logout for microsoft in an iframe
         * and wait for first load event that means the iframe has loaded at least one time
         */
        const iframe = document.createElement('iframe');
        this.renderer.setStyle(iframe, 'display', 'none');
        this.renderer.setAttribute(iframe, 'src', `https://login.microsoftonline.com/common/oauth2/logout`);
        return zip(of(this.renderer.appendChild(document.body, iframe)), fromEvent(iframe, 'load')).pipe(map(() => true), finalize(() => this.renderer.removeChild(document.body, iframe)), take(1));
    }
}
SSOStrategy.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: SSOStrategy, deps: [{ token: i1.AngularFireAuth }, { token: i0.RendererFactory2 }], target: i0.ɵɵFactoryTarget.Injectable });
SSOStrategy.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: SSOStrategy, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: SSOStrategy, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1.AngularFireAuth }, { type: i0.RendererFactory2 }]; } });
/**
 * auth strategy to run against firebase:emulator auth
 *
 */
class EmailPasswordStrategy {
    constructor(fireAuth) {
        this.fireAuth = fireAuth;
        this.identifier = 'emailandpassword';
    }
    login(userId, password) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.fireAuth.signInWithEmailAndPassword(userId, password);
        });
    }
    logout() {
        return from(this.fireAuth.signOut());
    }
}
EmailPasswordStrategy.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: EmailPasswordStrategy, deps: [{ token: i1.AngularFireAuth }], target: i0.ɵɵFactoryTarget.Injectable });
EmailPasswordStrategy.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: EmailPasswordStrategy, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: EmailPasswordStrategy, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1.AngularFireAuth }]; } });
/**
 * auth strategy for google
 *
 */
class GoogleAuthStrategy {
    constructor(fireAuth) {
        this.fireAuth = fireAuth;
        this.identifier = 'google';
    }
    login() {
        return __awaiter(this, void 0, void 0, function* () {
            this.googleProvider = new firebase.auth.GoogleAuthProvider();
            yield this.fireAuth.signInWithRedirect(this.googleProvider);
        });
    }
    logout() {
        return from(this.fireAuth.signOut());
    }
}
GoogleAuthStrategy.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: GoogleAuthStrategy, deps: [{ token: i1.AngularFireAuth }], target: i0.ɵɵFactoryTarget.Injectable });
GoogleAuthStrategy.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: GoogleAuthStrategy, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: GoogleAuthStrategy, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1.AngularFireAuth }]; } });
/**
 * auth strategy for iframe
 */
class IframeStrategy {
    constructor(fireAuth, router) {
        this.fireAuth = fireAuth;
        this.router = router;
        this.identifier = 'iframe';
    }
    getJwtToken() {
        if (!this.customToken) {
            return this.requestTokenFromParent().then((token) => {
                this.setToken(token);
                return token;
            });
        }
        else {
            return Promise.resolve(this.customToken);
        }
    }
    setToken(token) {
        this.customToken = token;
    }
    requestTokenFromParent() {
        const payload = { action: "getToken" };
        window.parent.postMessage(payload, '*');
        return this.listen();
    }
    listen() {
        return new Promise((resolve, reject) => {
            window.addEventListener("message", (event) => {
                if (!!event.data.token) {
                    this.setToken(event.data.token);
                    resolve(event.data.token);
                }
            }, false);
        });
    }
    login() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.fireAuth.signInAnonymously().catch(err => {
                var errorMessage = err.message;
                throw new Error(errorMessage);
            });
        });
    }
    logout() {
        return from(this.fireAuth.signOut());
    }
}
IframeStrategy.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: IframeStrategy, deps: [{ token: i1.AngularFireAuth }, { token: i2.Router }], target: i0.ɵɵFactoryTarget.Injectable });
IframeStrategy.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: IframeStrategy, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: IframeStrategy, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1.AngularFireAuth }, { type: i2.Router }]; } });

class AuthService {
    constructor(params, angularFireAuth, ssoStrategy, googleStrategy, emailPasswordStrategy, iframeStrategy) {
        this.params = params;
        this.angularFireAuth = angularFireAuth;
        this.ssoStrategy = ssoStrategy;
        this.googleStrategy = googleStrategy;
        this.emailPasswordStrategy = emailPasswordStrategy;
        this.iframeStrategy = iframeStrategy;
        this.logoutHooks = [];
        this.loginProviders = [];
        this.emailLoginArguments$ = new BehaviorSubject({
            email: null,
            password: null,
        });
        this.loginProviders = params.length ? params : [];
        this.user$ = new ReplaySubject(1);
        this.angularFireAuth.onAuthStateChanged((user) => {
            this.user$.next(user);
        });
        this.currentStrategy = ssoStrategy;
        if (this.isIframeLogin()) {
            this.initIframeAuth();
        }
    }
    addLogoutHook(hook) {
        this.logoutHooks.push(hook);
    }
    /*
     * checks if user is already authorized
     */
    isAuthorized() {
        return this.angularFireAuth.authState.pipe(map((user) => !!user), take(1));
    }
    /**
     * on authstate change
     */
    authstateChange() {
        return this.user$.pipe(map((user) => !!user));
    }
    /**
     * get token or current user for request
     */
    getJwtToken() {
        return this.angularFireAuth.idToken;
    }
    getIframeToken() {
        return this.iframeStrategy.getJwtToken();
    }
    /**
     * returns Firebase user
     */
    getUser() {
        return this.angularFireAuth.user;
    }
    /**
     * check if login provider is email and password
     */
    isEmailAndPasswordLogin(provider) {
        const token = this.emailPasswordStrategy.identifier;
        provider = provider.toLowerCase();
        if (provider === token) {
            this.currentStrategy = this.emailPasswordStrategy;
        }
        return provider === token;
    }
    /**
     * check if login provider is for google
     */
    isGoogleLogin(provider) {
        const token = this.googleStrategy.identifier;
        provider = provider.toLowerCase();
        if (provider === token) {
            this.currentStrategy = this.googleStrategy;
        }
        return provider === token;
    }
    /**
     * check if login provider is an iframe
     */
    isIframeLogin() {
        const token = this.iframeStrategy.identifier;
        return this.loginProviders.includes(token);
    }
    /**
     * init iframe
     */
    initIframeAuth() {
        this.currentStrategy = this.iframeStrategy;
        this.iframeStrategy.login();
    }
    /**
     * perform login action
     */
    login(provider) {
        return __awaiter(this, void 0, void 0, function* () {
            provider = provider.toLowerCase();
            if (this.isEmailAndPasswordLogin(provider)) {
                const { email, password } = this.emailLoginArguments$.value;
                return this.emailPasswordStrategy.login(email, password);
            }
            else if (this.isGoogleLogin(provider)) {
                return this.googleStrategy.login();
            }
            else {
                return this.ssoStrategy.login(provider);
            }
        });
    }
    /**
     * perform logout action
     */
    logout() {
        return logout(this.currentStrategy);
    }
    /**
     * resolve redirect data from activated route
     */
    resolveRedirectData(route) {
        var _a;
        const snapshot = route === null || route === void 0 ? void 0 : route.snapshot;
        const queryParam = snapshot === null || snapshot === void 0 ? void 0 : snapshot.queryParams;
        if (!!route) {
            if (Object.keys(queryParam).length > 0) {
                const parsed = queryString.parseUrl(queryParam === null || queryParam === void 0 ? void 0 : queryParam.returnUrl);
                const path = parsed === null || parsed === void 0 ? void 0 : parsed.url;
                const params = (_a = parsed === null || parsed === void 0 ? void 0 : parsed.query) !== null && _a !== void 0 ? _a : {};
                return { path: path !== null && path !== void 0 ? path : '/', queryParams: params };
            }
        }
        return { path: '/', queryParams: {} };
    }
}
AuthService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: AuthService, deps: [{ token: LOGIN_PARAMS, optional: true }, { token: i1.AngularFireAuth }, { token: SSOStrategy }, { token: GoogleAuthStrategy }, { token: EmailPasswordStrategy }, { token: IframeStrategy }], target: i0.ɵɵFactoryTarget.Injectable });
AuthService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: AuthService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: AuthService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () {
        return [{ type: Array, decorators: [{
                        type: Optional
                    }, {
                        type: Inject,
                        args: [LOGIN_PARAMS]
                    }] }, { type: i1.AngularFireAuth }, { type: SSOStrategy }, { type: GoogleAuthStrategy }, { type: EmailPasswordStrategy }, { type: IframeStrategy }];
    } });

class AuthGuard {
    constructor(router, authService) {
        this.router = router;
        this.authService = authService;
    }
    canActivate(route, state) {
        return this.authService.isAuthorized().pipe(tap((isAuthorized) => {
            if (!isAuthorized) {
                this.router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
                return false;
            }
            return true;
        }));
    }
}
AuthGuard.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: AuthGuard, deps: [{ token: i2.Router }, { token: AuthService }], target: i0.ɵɵFactoryTarget.Injectable });
AuthGuard.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: AuthGuard, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: AuthGuard, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i2.Router }, { type: AuthService }]; } });

const AuthProviderToken = new InjectionToken('angular fire auth provider');
/**
 * declare injection token which will consumed by jwt-interceptor
 * since this should be a bit more flexible we could check into this one
 * the uri which has been passed should added jwt token otherwise
 * event will pass
 */
const JWT_INTERCEPT_HOOK = new InjectionToken('authorization pre intercept hook for token');

class JwtInterceptor {
    constructor(interceptValidator, authService) {
        this.interceptValidator = interceptValidator;
        this.authService = authService;
    }
    intercept(request, next) {
        /**
         * @inject only on specific url
         */
        if (this.interceptValidator && this.interceptValidator(request.url)) {
            if (this.authService.isIframeLogin()) {
                return from(this.authService.getIframeToken()).pipe(switchMap((token) => {
                    return this.cloneRequest(request, token, next);
                }));
            }
            else {
                return from(this.authService.getJwtToken()).pipe(switchMap((token) => {
                    return this.cloneRequest(request, token, next);
                }));
            }
        }
        return next.handle(request);
    }
    cloneRequest(request, token, next) {
        if (token) {
            request = request.clone({
                setHeaders: { Authorization: "Bearer " + `${token}` },
            });
        }
        return next.handle(request);
    }
}
JwtInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: JwtInterceptor, deps: [{ token: JWT_INTERCEPT_HOOK }, { token: AuthService }], target: i0.ɵɵFactoryTarget.Injectable });
JwtInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: JwtInterceptor });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: JwtInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [JWT_INTERCEPT_HOOK]
                    }] }, { type: AuthService }];
    } });

function preInterceptorHookFactory(hook) {
    return hook ? (uri) => hook(uri) : () => true;
}
// @dynamic
class AiHubAuthModule {
    static forRoot(config) {
        var _a;
        if (config.authProvider) {
            config.params = [config.authProvider];
        }
        const providers = [
            /**
             * provide firebase options
             */
            {
                provide: FIREBASE_OPTIONS,
                useValue: config.firebase
            },
            {
                provide: JWT_INTERCEPT_HOOK,
                useFactory: () => preInterceptorHookFactory(config.jwtInterceptHook)
            },
            /**
             * provide jwt interceptor so we send allways the JWT token with
             * every request
             */
            {
                provide: HTTP_INTERCEPTORS,
                useClass: JwtInterceptor,
                multi: true
            },
            /**
             * params which will be used by default by
             * login service
             */
            {
                provide: LOGIN_PARAMS,
                useValue: config.params
            },
            /**
             * provide an auth strategy how we want to connect
             * like microsoft azur, google, or firebase:emulator
             */
            {
                provide: AUTH_STRATEGY,
                useExisting: (_a = config.strategy) !== null && _a !== void 0 ? _a : SSOStrategy,
            }
        ];
        if (config.emulate) {
            providers.push({
                provide: USE_EMULATOR,
                useValue: ['localhost', 9099]
            });
        }
        return { ngModule: AiHubAuthModule, providers };
    }
}
AiHubAuthModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: AiHubAuthModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AiHubAuthModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: AiHubAuthModule, imports: [CommonModule,
        AngularFireAuthModule,
        AngularFireModule] });
AiHubAuthModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: AiHubAuthModule, providers: [
        AuthService,
        AuthGuard
    ], imports: [[
            CommonModule,
            AngularFireAuthModule,
            AngularFireModule
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: AiHubAuthModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        AngularFireAuthModule,
                        AngularFireModule
                    ],
                    providers: [
                        AuthService,
                        AuthGuard
                    ]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AUTH_STRATEGY, AiHubAuthModule, AuthGuard, AuthProviderToken, AuthService, EmailPasswordStrategy, GoogleAuthStrategy, IframeStrategy, JWT_INTERCEPT_HOOK, JwtInterceptor, LOGIN_PARAMS, SSOStrategy, logout };
